import { 
  CognitoIdentityProviderClient, 
  InitiateAuthCommand, 
  RespondToAuthChallengeCommand,
  ChangePasswordCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  AdminSetUserPasswordCommand,
  NotAuthorizedException,
  UserNotFoundException,
  PasswordResetRequiredException,
  ChallengeName,
  GlobalSignOutCommand
} from "@aws-sdk/client-cognito-identity-provider";
import { configService } from './configService';

// Cognitoクライアントの初期化（遅延初期化）
let cognitoClient: CognitoIdentityProviderClient | null = null;

const getCognitoClient = async (): Promise<CognitoIdentityProviderClient> => {
  if (cognitoClient) return cognitoClient;
  
  const config = await configService.loadConfig();
  
  cognitoClient = new CognitoIdentityProviderClient({ 
    region: config.cognito.userPoolId.split('_')[0], // リージョンはUserPoolIdから抽出
    // 必要に応じて認証情報を設定
    // credentials: fromCognitoIdentityPool({...})
  });
  
  return cognitoClient;
};

// セッション情報の保存場所
const getStorage = (rememberMe: boolean) => rememberMe ? localStorage : sessionStorage;

// 認証サービス
export const authService = {
  /**
   * ログイン処理
   * @param username ユーザー名（メールアドレスまたはユーザーID）
   * @param password パスワード
   * @param rememberMe ログイン状態を保持するかどうか
   * @returns ログイン結果
   */
  async login(username: string, password: string, rememberMe: boolean = false) {
    try {
      // 既存のセッションをクリア（ユーザー切り替え時の問題を解決）
      await this.logout();
      
      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      
      // ログインリクエスト
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: config.cognito.userPoolClientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      const response = await client.send(command);

      // 認証チャレンジがある場合（初回ログインなど）
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challengeName: response.ChallengeName,
          session: response.Session,
          message: 'パスワードの変更が必要です',
        };
      }

      // 認証成功
      if (response.AuthenticationResult) {
        const { IdToken, AccessToken, RefreshToken, ExpiresIn } = response.AuthenticationResult;
        
        // トークンを保存
        const storage = getStorage(rememberMe);
        storage.setItem('idToken', IdToken || '');
        storage.setItem('accessToken', AccessToken || '');
        storage.setItem('refreshToken', RefreshToken || '');
        storage.setItem('tokenExpiry', (Date.now() + (ExpiresIn || 3600) * 1000).toString());
        
        // ユーザー情報を取得（実際の実装ではユーザー情報APIを呼び出す）
        // この例では、トークンからユーザー情報を抽出する簡易的な実装
        const userInfo = this.extractUserInfoFromToken(IdToken || '');
        storage.setItem('userInfo', JSON.stringify(userInfo));
        
        return {
          success: true,
          user: userInfo,
          token: IdToken,
        };
      }

      return {
        success: false,
        message: '認証に失敗しました',
      };
    } catch (error) {
      // console.error('Login error:', error);
      
      if (error instanceof NotAuthorizedException) {
        return {
          success: false,
          message: `企業ID、ユーザーID、またはパスワードが正しくありません\n${error.message}`,
        };
      } else if (error instanceof UserNotFoundException) {
        return {
          success: false,
          message: `企業ID、ユーザーID、またはパスワードが正しくありません\n${error.message}`,
          // message: 'ユーザーが見つかりません',
        };
      } else if (error instanceof PasswordResetRequiredException) {
        return {
          success: false,
          message: 'パスワードのリセットが必要です',
          requiresPasswordReset: true,
        };
      }
      
      return {
        success: false,
        message: 'ログイン中にエラーが発生しました\n' + (error instanceof Error ? error.message : '不明なエラー'),
        error,
      };
    }
  },

  /**
   * 初回ログイン時のパスワード変更
   * @param username ユーザー名
   * @param newPassword 新しいパスワード
   * @param session 認証セッション
   * @param rememberMe ログイン状態を保持するかどうか
   * @returns パスワード変更結果
   */
  async completeNewPassword(username: string, newPassword: string, session: string | undefined, rememberMe: boolean = false) {
    if (!session) {
      return {
        success: false,
        message: '認証セッションが見つかりません',
      };
    }
    try {
      // 既存のセッションをクリア（ユーザー切り替え時の問題を解決）
      this.clearLocalStorageAndSessionStorage();
      
      // sessionはここで非nullであることが保証されているので、型アサーションを使用
      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      
      const command = new RespondToAuthChallengeCommand({
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: config.cognito.userPoolClientId,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
        Session: session as string,
      });

      const response = await client.send(command);

      // 認証成功
      if (response.AuthenticationResult) {
        const { IdToken, AccessToken, RefreshToken, ExpiresIn } = response.AuthenticationResult;
        
        // トークンを保存
        const storage = getStorage(rememberMe);
        storage.setItem('idToken', IdToken || '');
        storage.setItem('accessToken', AccessToken || '');
        storage.setItem('refreshToken', RefreshToken || '');
        storage.setItem('tokenExpiry', (Date.now() + (ExpiresIn || 3600) * 1000).toString());
        
        // ユーザー情報を取得
        const userInfo = this.extractUserInfoFromToken(IdToken || '');
        storage.setItem('userInfo', JSON.stringify(userInfo));
        
        return {
          success: true,
          user: userInfo,
          token: IdToken,
        };
      }

      return {
        success: false,
        message: 'パスワード変更に失敗しました',
      };
    } catch (error) {
      console.error('Complete new password error:', error);
      return {
        success: false,
        message: `パスワード変更中にエラーが発生しました`,
        error,
      };
    }
  },

  /**
   * パスワード変更（ログイン済みユーザー）
   * @param oldPassword 古いパスワード
   * @param newPassword 新しいパスワード
   * @returns パスワード変更結果
   */
  async changePassword(oldPassword: string, newPassword: string) {
    try {
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      
      if (!accessToken) {
        return {
          success: false,
          message: '認証情報が見つかりません。再度ログインしてください。',
        };
      }

      const client = await getCognitoClient();
      
      const command = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      });

      await client.send(command);

      return {
        success: true,
        message: 'パスワードが正常に変更されました',
      };
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        message: 'パスワード変更中にエラーが発生しました',
        error,
      };
    }
  },

  /**
   * SSO ワンクリックログイン（カスタム認証）
   * @param companyId 企業コード（company）
   * @param givenUserId ログインID（user）
   * @param ssoToken ログイン用ワンタイムトークン（redirectUrl の query token）
   * @param rememberMe ログイン状態を保持するか
   */
  async loginWithSso(companyId: string, givenUserId: string, ssoToken: string, rememberMe: boolean = false) {
    try {
      await this.logout();
      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      const username = `${companyId}#${givenUserId}`;

      const initiateResponse = await client.send(new InitiateAuthCommand({
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: config.cognito.userPoolClientId,
        AuthParameters: { USERNAME: username },
        ClientMetadata: { ssoToken },
      }));

      let session = initiateResponse.Session;
      let challengeName = initiateResponse.ChallengeName;

      if (challengeName === 'CUSTOM_CHALLENGE' && session) {
        const respondResponse = await client.send(new RespondToAuthChallengeCommand({
          ChallengeName: 'CUSTOM_CHALLENGE',
          ClientId: config.cognito.userPoolClientId,
          ChallengeResponses: {
            USERNAME: username,
            ANSWER: ssoToken,
          },
          Session: session,
          ClientMetadata: { ssoToken },
        }));
        session = respondResponse.Session;
        challengeName = respondResponse.ChallengeName;

        if (respondResponse.AuthenticationResult) {
          const { IdToken, AccessToken, RefreshToken, ExpiresIn } = respondResponse.AuthenticationResult;
          const storage = getStorage(rememberMe);
          storage.setItem('idToken', IdToken || '');
          storage.setItem('accessToken', AccessToken || '');
          storage.setItem('refreshToken', RefreshToken || '');
          storage.setItem('tokenExpiry', (Date.now() + (ExpiresIn || 3600) * 1000).toString());
          const userInfo = this.extractUserInfoFromToken(IdToken || '');
          storage.setItem('userInfo', JSON.stringify(userInfo));
          return { success: true, user: userInfo, token: IdToken };
        }
      }

      return { success: false, message: 'SSOログインに失敗しました' };
    } catch (error) {
      return {
        success: false,
        message: 'SSOログイン中にエラーが発生しました\n' + (error instanceof Error ? error.message : '不明なエラー'),
        error,
      };
    }
  },

  /**
   * パスワードリセットの開始
   * @param username ユーザー名（メールアドレス）
   * @returns パスワードリセット開始結果
   */
  async forgotPassword(username: string) {
    try {
      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      
      const command = new ForgotPasswordCommand({
        ClientId: config.cognito.userPoolClientId,
        Username: username,
      });

      await client.send(command);

      return {
        success: true,
        message: 'パスワードリセット用のコードが送信されました',
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: 'パスワードリセットの処理中にエラーが発生しました',
        error,
      };
    }
  },

  /**
   * パスワードリセットの確認
   * @param username ユーザー名（メールアドレス）
   * @param code 確認コード
   * @param newPassword 新しいパスワード
   * @returns パスワードリセット確認結果
   */
  async confirmForgotPassword(username: string, code: string, newPassword: string) {
    try {
      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      
      const command = new ConfirmForgotPasswordCommand({
        ClientId: config.cognito.userPoolClientId,
        Username: username,
        ConfirmationCode: code,
        Password: newPassword,
      });

      await client.send(command);

      return {
        success: true,
        message: 'パスワードが正常にリセットされました',
      };
    } catch (error) {
      console.error('Confirm forgot password error:', error);
      return {
        success: false,
        message: 'パスワードリセットの確認中にエラーが発生しました',
        error,
      };
    }
  },

  /**
   * 管理者によるユーザーパスワードのリセット
   * @param username ユーザー名（givenUserId）
   * @param newPassword 新しいパスワード
   * @param permanent パスワードを恒久的にするか（falseの場合、次回ログイン時に変更が必要）
   * @returns パスワードリセット結果
   */
  async adminResetUserPassword(username: string, newPassword: string, permanent: boolean = false) {
    try {
      const config = await configService.loadConfig();
      
      // IDトークンを取得
      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        return {
          success: false,
          message: '認証情報が見つかりません。再度ログインしてください。',
        };
      }

      // 企業IDを取得
      const userInfo = this.getCurrentUser();
      if (!userInfo || !userInfo.companyId) {
        return {
          success: false,
          message: 'ユーザー情報が見つかりません。再度ログインしてください。',
        };
      }

      // ユーザー名に企業IDを付加（companyId#givenUserId形式）
      const fullUsername = `${userInfo.companyId}#${username}`;

      // REST APIを呼び出す
      // apiUrlの末尾のスラッシュを考慮して、パスを正規化
      const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
      const response = await fetch(`${baseUrl}/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          username: fullUsername,
          newPassword,
          permanent
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'パスワードリセット中にエラーが発生しました',
        };
      }

      return {
        success: true,
        message: data.message || 'ユーザーのパスワードが正常にリセットされました',
      };
    } catch (error) {
      console.error('Admin reset password error:', error);
      return {
        success: false,
        message: 'パスワードリセット中にエラーが発生しました',
        error,
      };
    }
  },

  /**
   * ローカルストレージとセッションストレージをクリアする内部メソッド
   */
  clearLocalStorageAndSessionStorage() {
    // ローカルストレージとセッションストレージの両方をクリア
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userInfo');
    
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('userInfo');
  },

  /**
   * ログアウト処理
   * @returns ログアウト結果のPromise
   */
  async logout() {
    try {
      // アクセストークンを取得
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      
      if (accessToken) {
        // Cognitoクライアントを取得
        const client = await getCognitoClient();
        
        // サーバー側でセッションを無効化
        const command = new GlobalSignOutCommand({
          AccessToken: accessToken
        });
        
        try {
          await client.send(command);
          // console.log('Successfully signed out from Cognito');
        } catch (error) {
          console.error('Error during global sign out:', error);
          // エラーが発生しても続行（ローカルのクリーンアップは必要）
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ローカルストレージとセッションストレージをクリア
      this.clearLocalStorageAndSessionStorage();
    }
  },

  /**
   * 認証状態の確認
   * @returns 認証状態
   */
  isAuthenticated() {
    const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
    const expiry = localStorage.getItem('tokenExpiry') || sessionStorage.getItem('tokenExpiry');
    
    if (!token || !expiry) {
      return false;
    }
    
    // トークンの有効期限をチェック
    return Date.now() < parseInt(expiry, 10);
  },

  /**
   * トークンの有効期限をチェック
   * @returns トークンが有効期限切れかどうか
   */
  isTokenExpired() {
    const expiry = localStorage.getItem('tokenExpiry') || sessionStorage.getItem('tokenExpiry');
    const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
    
    if (!expiry || !token) {
      return true;
    }
    
    // トークンの有効期限をチェック（5分前に期限切れと判断）
    return Date.now() > parseInt(expiry, 10) - 5 * 60 * 1000;
  },

  /**
   * トークンの更新
   * @returns 更新結果
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        return {
          success: false,
          message: 'リフレッシュトークンが見つかりません',
        };
      }

      const config = await configService.loadConfig();
      const client = await getCognitoClient();
      
      // リフレッシュトークンを使用して認証
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: config.cognito.userPoolClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      const response = await client.send(command);

      // 認証成功
      if (response.AuthenticationResult) {
        const { IdToken, AccessToken, ExpiresIn } = response.AuthenticationResult;
        
        // トークンを保存（リフレッシュトークンは更新されないので保存しない）
        const isLocalStorage = localStorage.getItem('refreshToken') !== null;
        const storage = isLocalStorage ? localStorage : sessionStorage;
        
        storage.setItem('idToken', IdToken || '');
        storage.setItem('accessToken', AccessToken || '');
        storage.setItem('tokenExpiry', (Date.now() + (ExpiresIn || 3600) * 1000).toString());
        
        return {
          success: true,
          token: IdToken,
        };
      }

      return {
        success: false,
        message: 'トークンの更新に失敗しました',
      };
    } catch (error) {
      console.error('Token refresh error:', error);
      
      // リフレッシュトークンが無効な場合はログアウト
      if (error instanceof NotAuthorizedException) {
        this.logout();
        return {
          success: false,
          message: 'セッションが無効になりました。再度ログインしてください。',
          requireRelogin: true,
        };
      }
      
      return {
        success: false,
        message: 'トークンの更新中にエラーが発生しました',
        error,
      };
    }
  },

  /**
   * 現在のユーザー情報を取得
   * @returns ユーザー情報
   */
  getCurrentUser() {
    const userInfoStr = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  },

  /**
   * JWTトークンからユーザー情報を抽出する簡易的な実装
   * @param token IDトークン
   * @returns ユーザー情報
   */
  extractUserInfoFromToken(token: string) {
    try {
      // トークンの形式: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }
      
      // Base64デコード（パディングを追加）
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      const paddedBase64 = pad ? base64 + '='.repeat(4 - pad) : base64;
      
      // UTF-8でデコード
      const jsonStr = decodeURIComponent(
        atob(paddedBase64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonStr);
      
      // デバッグ用：トークンのペイロードをコンソールに出力
      // console.log('Token payload:', payload);
      // console.log('Nickname raw:', payload.nickname);
      
      return {
        userId: payload['cognito:username'],
        givenUserId: payload['custom:givenUserId'],
        email: payload.email,
        displayName: payload.nickname,
        companyId: payload['custom:companyId'],
        userRole: payload['custom:userRole'],
        department: payload['custom:department'],
        profileId: payload['custom:profileId'] || undefined,
        managementScope: payload['custom:managementScope'] || undefined,
        locked: payload['custom:locked'] === 'true' || payload['custom:locked'] === true,
      };
    } catch (error) {
      console.error('Token parsing error:', error);
      return null;
    }
  },
};
